import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { gql } from '@apollo/client';

import { useFlags } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { token } from '@atlaskit/tokens';

import { LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

const i18n = defineMessages({
	flagTitle: {
		id: 'experiment-land-in-sample-space-xflow.database.flag.title',
		defaultMessage: 'We’ve landed you in a database',
		description: 'Title text informing the user they are viewing a database',
	},
	flagBody: {
		id: 'experiment-land-in-sample-space-xflow.database.flag.body',
		defaultMessage:
			'Get started easily by trying out a database template from the right side panel.',
		description: 'Body text displayed in a flag telling the user to pick a database template',
	},
});

interface Props {
	spaceKey: string;
}

/**
 * This flag should only be displayed if the user selects to land in a sample database.
 * Once any sample content (whiteboard, live, database) flag is displayed, we don't want to display others.
 */
const DatabaseFlagComponent: React.FC<Props> = ({ spaceKey }) => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS)]);

	const { hasSeenSampleContentFlag } = deserializeState(onboardingStateData);

	if (isUnauthorizedError(onboardingStateError)) {
		markErrorAsHandled(onboardingStateError);
	}

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error } = useQuery(
		gql`
			query DatabaseFlagSpaceTypeQuery($spaceKey: String) {
				space(key: $spaceKey) {
					type
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: hasSeenSampleContentFlag || onboardingStateLoading || onboardingStateError,
		},
	);

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	const spaceType = data?.space.type;

	useEffect(() => {
		if (spaceType === 'xflow_sample_space' && !hasSeenSampleContentFlag) {
			showFlag({
				title: formatMessage(i18n.flagTitle),
				description: formatMessage(i18n.flagBody),
				icon: <CheckCircleIcon label="check" color={token('color.icon.accent.green')} />,
			});

			// Mark as dismissed as long as the user has seen the flag
			setOnboardingState({
				key: LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS.hasSeenSampleContentFlag,
				value: 'true',
			});
		}
	}, [showFlag, formatMessage, spaceType, hasSeenSampleContentFlag, setOnboardingState]);

	return <></>;
};

export const DatabaseFlag = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(DatabaseFlagComponent);
